import { inject, Injectable } from '@angular/core'
import { combineLatest, forkJoin, map, Observable, of } from 'rxjs'
import { SeFeApiService } from 'se-fe-api'

export type SearchParams = Record<string, any>
export type SearchFn = (term: string) => Observable<SearchResult[]>

export interface SearchConfig {
  resourceSystem: string
  resourceType: string
  params?: SearchParams // required to search via API (if present)
  resources?: SearchResource[] // show preloaded data (will still search via API if params are present)
}

export interface SearchResource {
  resourceSystem: string
  resourceType: string
  resourceId: string
  name: string
  category?: string
  resource?: any
}

export interface SearchResult extends SearchResource {}
export class SearchResult {
  public value: string

  constructor(data: any) {
    Object.assign(this, data)
    this.value = `${data.resourceSystem}:${data.resourceType}:${data.resourceId}`
  }
}

const DEFAULT_HEADERS = { Accept: 'application/vnd.ngin-api.v2, application/json' }

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  public api = inject(SeFeApiService)
  public resourceHandlers = {
    'TeamService:Team': 'teams',
    'UserService:Persona': 'personas',
    'Ngin:Group': 'groups'
  }

  // EX: searchService.search('test', [ resourceSystem: 'TeamService:Team', params: { season_id: 123, org_id: 456 } })
  public search(term: string, resourceSystemParams: SearchConfig[], type?: string): Observable<SearchResult[]> {
    const observables: Observable<SearchResult[]>[] = []

    resourceSystemParams.forEach(({ resourceSystem, resourceType, params, resources }) => {
      const handler = this.resourceHandlers[`${resourceSystem}:${resourceType}`]
      if (type && handler !== type) return
      observables.push(this[handler](term, params, resources))
    })

    return forkJoin(observables)
      .pipe(map(results => results.flat().sort((a, b) => (
        // TODO: do we want to sort differently by filter type?
        this.compareProp('resourceType', a, b) ||
        this.compareProp('category', a, b) ||
        this.compareProp('name', a, b) ||
        this.compareProp('id', a, b)
      ))))
  }

  public parseTeam(data: any): SearchResource {
    const { id: resourceId, name, division_name: category} = data
    const resourceSystem = 'TeamService'
    const resourceType = 'Team'
    return { resourceSystem, resourceType, resourceId, name, category, resource: data }
  }

  public teams(
    term: string,
    params: SearchParams,
    resources: SearchResource[]
  ): Observable<SearchResult[]> {
    const url = 'v3/roster_search'
    return this.searchOrDefaultResults(term, resources, (search) => (this.api
      .get<SearchResult[]>(url, { params: { ...params, search }, headers: DEFAULT_HEADERS })
      .pipe(map(response => response.result.map(item => new SearchResult(this.parseTeam(item)))))
    ))
  }

  public getTeam(params: any): Observable<SearchResult> {
    const url = 'v3/roster_search'
    return this.api
      .get<SearchResult[]>(url, { params, headers: DEFAULT_HEADERS })
      .pipe(map(response => new SearchResult(this.parseTeam(response.result[0] || {}))))
  }

  public parsePersona(data: any): any {
    const { id: resourceId, name  } = data
    const resourceSystem = 'UserService'
    const resourceType = 'Persona'
    const category = data.contact_type || 'Persona'
    return { resourceSystem, resourceType, resourceId, name, category, resource: data }
  }

  public personas(
    term: string,
    params: SearchParams,
    resources: SearchResource[]
  ): Observable<SearchResult[]> {
    const url ='contact_list/filter'
    return this.searchOrDefaultResults(term, resources, (search) => (this.api
      .post<any[]>(url, {...params, search}, { headers: DEFAULT_HEADERS, withCredentials: true })
      .pipe(map(response => response.result.map(item => new SearchResult(this.parsePersona(item)))))
    ))
  }

  public getPersona(params: any): Observable<SearchResult> {
    const url = 'contact_list/filter'
    return this.api
      .post<any[]>(url, params, { headers: DEFAULT_HEADERS, withCredentials: true })
      .pipe(map(response => new SearchResult(this.parsePersona(response.result[0] || {}))))
  }

  public parseGroup(data: any): SearchResource {
    const { id: resourceId, name, group_type: category } = data
    const resourceSystem = 'GroupService'
    const resourceType = 'Group'
    return { resourceSystem, resourceType, resourceId, name, category, resource: data, }
  }

  public groups(
    term: string,
    params: SearchParams,
    resources: SearchResource[]
  ): Observable<SearchResult[]> {
    const url = 'groups'
    return this.searchOrDefaultResults(term, resources, (search) => (this.api
      .get<SearchResult[]>(url, { params: { ...params, name: search }, headers: DEFAULT_HEADERS })
      .pipe(map(response => response.result.map(item => new SearchResult(this.parseGroup(item))))))
    )
  }

  public getGroup(id: string): Observable<SearchResult> {
    const url = `groups/${id}`
    return this.api
      .get<SearchResult>(url, { headers: DEFAULT_HEADERS })
      .pipe(map(response => new SearchResult(this.parseGroup(response.result))))
  }

  private searchOrDefaultResults(term: string, resources: SearchResource[], fn: SearchFn): Observable<SearchResult[]> {
    const t = term.trim().toLowerCase()

    if (!t) return of((resources || []).map(r => new SearchResult(r)))
    if (!resources) return fn(t)
    const local = this.localSearch(t, resources)
    const api = fn(t)
    return this.getMergedResources(local, api)
  }

  private getMergedResources(local: Observable<SearchResult[]>, api: Observable<SearchResult[]>): Observable<SearchResult[]> {
    return combineLatest([local, api]).pipe(
      map(([a, b]) => {
        const merged = [...a, ...b]
        const deduped = merged.filter(
          (item, index, self) =>
            index === self.findIndex(t => t.resourceId === item.resourceId)
        )
        return deduped
      })
    )
  }

  private localSearch(term: string, resources: SearchResource[]): Observable<SearchResult[]> {
    return of(resources.filter(r => r.name.toLowerCase().includes(term)).map(r => new SearchResult(r)))
  }

  private compareProp(prop: string, a: any, b: any): any {
    const lower = (s: any) => s?.toLowerCase() || ''
    return lower(a[prop]).localeCompare(lower(b[prop]))
  }

}
